// client/src/components/admin/PatientManagement/AdminPatientDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import adminService from '../../services/adminService';
import '../../styles/components/admin/AdminPatientDetails.css';
import AdminDoctorAppointmentsTab from './AdminDoctorAppointmentsTab';



const AdminPatientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId');
  
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('registration');
  const [tenant, setTenant] = useState(null);

  // Mood check-ins related states
  const [moodData, setMoodData] = useState(null);
  const [moodLoading, setMoodLoading] = useState(false);
  const [moodError, setMoodError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(7);

  // 🆕 NEW: Journal related states
  const [journalData, setJournalData] = useState(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState(null);

  // Add these state variables to your main component
const [doctorAssociations, setDoctorAssociations] = useState([]);
const [doctorAssociationsLoading, setDoctorAssociationsLoading] = useState(false);
const [doctorAssociationsError, setDoctorAssociationsError] = useState(null);

  // Mood SVG URLs and colors
  const moodSvgUrls = {
    great: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_Great_anfp2e.svg',
    good: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_good_ldcpo4.svg',
    okay: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_okay_tcf9cp.svg',
    struggling: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_struggling_regocn.svg',
    upset: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_upset_pqskxp.svg'
  };

  const moodColors = {
    great: '#4CAF50',
    good: '#8BC34A', 
    okay: '#FFC107',
    struggling: '#FF9800',
    upset: '#F44336'
  };

  // Helper function to safely display values
  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null') {
      return 'Not provided';
    }
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    // Handle numbers (including 0)
    if (typeof value === 'number') {
      return value.toString();
    }
    // Handle strings
    return value.toString().trim() || 'Not provided';
  };

  // Fetch patient details
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (!id) {
        setError('Patient ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // First, get tenant info if tenantId is provided
        if (tenantId) {
          try {
            const tenantResponse = await adminService.getTenantById(tenantId);
            if (tenantResponse.success && tenantResponse.data) {
              setTenant(tenantResponse.data);
            }
          } catch (tenantError) {
            console.warn('Could not fetch tenant details:', tenantError);
          }
        }

        // Fetch patient details
        const response = await adminService.getPatientById(id, tenantId);
        
        console.log('🔍 ADMIN PATIENT DETAILS RESPONSE:', response);
        
        if (response.success && response.data) {
          console.log('✅ Using response.data:', response.data);
          setPatient(response.data);
        } else if (response.patient) {
          console.log('✅ Using response.patient:', response.patient);
          setPatient(response.patient);
        } else if (response._id) {
          console.log('✅ Using response directly:', response);
          setPatient(response);
        } else {
          console.error('❌ Unexpected response structure:', response);
          setError('Patient not found or unexpected response format');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error fetching admin patient details:', error);
        setError('Failed to load patient details. Please try again.');
        setLoading(false);
      }
    };

    fetchPatientDetails();
  }, [id, tenantId]);

  // Fetch patient mood data
  const fetchPatientMoodData = async () => {
    try {
      setMoodLoading(true);
      setMoodError(null);
      
      console.log(`🔍 ADMIN: Fetching mood data for patient ${id} (${selectedDays} days)`);
      
      const response = await adminService.getIndividualPatientMoodAnalytics(id, selectedDays);
      
      if (response.success && response.data) {
        console.log('✅ ADMIN: Mood data received:', response.data);
        setMoodData(response.data);
      } else {
        console.log('📝 ADMIN: No mood data found');
        setMoodData(null);
      }
    } catch (error) {
      console.error('❌ ADMIN: Error fetching mood data:', error);
      setMoodError('Failed to load mood check-in data');
      setMoodData(null);
    } finally {
      setMoodLoading(false);
    }
  };

  // 🆕 NEW: Fetch patient journal data
 const fetchPatientJournalData = async () => {
  try {
    setJournalLoading(true);
    setJournalError(null);
    
    const response = await adminService.getIndividualPatientJournalEntries(id, selectedDays);
    
    // Debug the raw response
    console.log('RAW RESPONSE:', response);
    
    if (response.success && response.data) {
      // The backend returns: { success: true, data: { entries: [...], summary: {...} } }
      // So response.data should already have the correct structure
      setJournalData(response.data);
      console.log('JOURNAL DATA SET TO:', response.data);
    } else {
      console.log('NO VALID RESPONSE');
      setJournalData(null);
    }
  } catch (error) {
    console.error('ERROR FETCHING JOURNAL DATA:', error);
    setJournalError('Failed to load journal entries');
    setJournalData(null);
  } finally {
    setJournalLoading(false);
  }
};

  // Fetch mood data when tab becomes active or days filter changes
  useEffect(() => {
    if (activeTab === 'mood-checkins' && patient) {
      fetchPatientMoodData();
    }
  }, [activeTab, patient, selectedDays]);

  // 🆕 NEW: Fetch journal data when tab becomes active or days filter changes
  useEffect(() => {
    if (activeTab === 'journal' && patient) {
      fetchPatientJournalData();
    }
  }, [activeTab, patient, selectedDays]);

  useEffect(() => {
  if (activeTab === 'journal') {
    console.log('FORCING JOURNAL FETCH');
    fetchPatientJournalData();
  }
}, [activeTab]);

  const handleBack = () => {
    navigate('/admin/users');
  };

  const handleEdit = () => {
    navigate(`/admin/users/${id}/edit${tenantId ? `?tenantId=${tenantId}` : ''}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this patient? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await adminService.deletePatient(id, tenantId);
      
      if (response.success) {
        alert('Patient deleted successfully');
        navigate('/admin/users');
      } else {
        throw new Error(response.message || 'Failed to delete patient');
      }
    } catch (err) {
      console.error('Error deleting patient:', err);
      alert(`Error: ${err.message || 'An error occurred while deleting the patient'}`);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading-container">
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error-container">
        <div className="admin-error-alert">
          <span className="admin-error-icon">⚠️</span>
          <span className="admin-error-message">{error}</span>
        </div>
        <button className="admin-primary-button" onClick={handleBack}>
          Back to Patient Management
        </button>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="admin-error-container">
        <div className="admin-error-alert">
          <span className="admin-error-icon">❌</span>
          <span className="admin-error-message">Patient not found</span>
        </div>
        <button className="admin-primary-button" onClick={handleBack}>
          Back to Patient Management
        </button>
      </div>
    );
  }

  return (
    <div className="admin-patient-details-container">
      {/* Header */}
      <div className="admin-patient-header">
        <div className="admin-header-left">
          <button className="admin-back-button" onClick={handleBack}>
            <span className="admin-back-icon">←</span>
          </button>
          <div className="admin-patient-info">
            <h1 className="admin-patient-name">
              {patient.firstName} {patient.lastName}
            </h1>
            <p className="admin-patient-email">{patient.email}</p>
          </div>
        </div>
        <div className="admin-header-right">
          <div className="admin-status-badge admin-active">
            ● {patient.accountStatus || 'Active'}
          </div>
          <div className="admin-registration-date">
            Registration Date: {patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Unknown'}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tab-navigation">
        <button 
          className={`admin-tab-button ${activeTab === 'registration' ? 'active' : ''}`}
          onClick={() => setActiveTab('registration')}
        >
          Registration
        </button>
        <button 
          className={`admin-tab-button ${activeTab === 'onboarding' ? 'active' : ''}`}
          onClick={() => setActiveTab('onboarding')}
        >
          Onboarding
        </button>
        <button 
          className={`admin-tab-button ${activeTab === 'mood-checkins' ? 'active' : ''}`}
          onClick={() => setActiveTab('mood-checkins')}
        >
          Mood Check-ins
        </button>
        {/* 🆕 NEW: Journal tab button */}
        <button 
          className={`admin-tab-button ${activeTab === 'journal' ? 'active' : ''}`}
          onClick={() => setActiveTab('journal')}
        >
          Journal Entries
        </button>

        <button 
          className={`admin-tab-button ${activeTab === 'doctor-appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('doctor-appointments')}
        >
          Doctor Appointments
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-tab-content">
        {activeTab === 'registration' && (
          <AdminRegistrationTab patient={patient} displayValue={displayValue} />
        )}
        {activeTab === 'onboarding' && (
          <AdminOnboardingTab patient={patient} displayValue={displayValue} />
        )}
        {activeTab === 'mood-checkins' && (
          <AdminMoodCheckinsTab 
            patient={patient}
            moodData={moodData}
            moodLoading={moodLoading}
            moodError={moodError}
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            fetchPatientMoodData={fetchPatientMoodData}
            moodSvgUrls={moodSvgUrls}
            moodColors={moodColors}
            tenantId={tenantId}
          />
        )}
        {/* 🆕 NEW: Journal tab content */}
        {activeTab === 'journal' && (
          <AdminJournalTab 
            patient={patient}
            journalData={journalData}
            journalLoading={journalLoading}
            journalError={journalError}
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            fetchPatientJournalData={fetchPatientJournalData}
          />
        )}
        {activeTab === 'doctor-appointments' && (
            <AdminDoctorAppointmentsTab 
              patient={patient}
              patientId={id}
              tenantId={tenantId}
            />
          )}
      </div>
    </div>
  );
};

// Registration Tab Component
const AdminRegistrationTab = ({ patient, displayValue }) => {
  return (
    <div className="admin-tab-panel">
      <div className="admin-section-card">
        <div className="admin-profile-section">
           <p className="admin-photo-label">Profile Photo</p>
          <div className="admin-profile-photo-section">
            <div className="admin-profile-photo">
              {patient.profilePicture && patient.profilePicture !== 'default-profile.png' ? (
                <img 
                  src={patient.profilePicture} 
                  alt="Profile" 
                  className="admin-profile-image"
                />
              ) : (
                <div className="admin-default-avatar">
                  {patient.firstName?.[0] || 'P'}
                </div>
              )}
            </div>
          </div>

          <div className="admin-profile-details">
            <div className="admin-detail-row">
              <div className="admin-detail-group">
                <label className="admin-detail-label">First Name</label>
                <div className="admin-detail-value">{displayValue(patient.firstName)}</div>
              </div>
              <div className="admin-detail-group">
                <label className="admin-detail-label">Last Name</label>
                <div className="admin-detail-value">{displayValue(patient.lastName)}</div>
              </div>
            </div>

            <div className="admin-detail-row">
              <div className="admin-detail-group">
                <label className="admin-detail-label">Email Address</label>
                <div className="admin-detail-value">{displayValue(patient.email)}</div>
              </div>
              <div className="admin-detail-group">
                <label className="admin-detail-label">Linked Google Account</label>
                <div className="admin-detail-value">
                  {patient.googleId ? patient.email : 'Not connected'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Onboarding Tab Component
const AdminOnboardingTab = ({ patient, displayValue }) => {
  return (
    <div className="admin-tab-panel">
      {/* Part 1: Personal Information */}
      <div className="admin-section-card">
        <h3 className="admin-section-title">Part 1: Personal Information</h3>
        <div className="admin-personal-info-grid">
          <div className="admin-detail-group">
                <label className="admin-detail-label">First Name</label>
                <div className="admin-detail-value">{displayValue(patient.firstName)}</div>
              </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Middle Name</label>
            <div className="admin-detail-value">{displayValue(patient.middleName)}</div>
          </div>
          <div className="admin-detail-group">
                <label className="admin-detail-label">Last Name</label>
                <div className="admin-detail-value">{displayValue(patient.lastName)}</div>
              </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Nickname</label>
            <div className="admin-detail-value">{displayValue(patient.nickname)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Birthdate</label>
            <div className="admin-detail-value">
              {patient.birthdate ? new Date(patient.birthdate).toLocaleDateString() : 'Not provided'}
            </div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Age</label>
            <div className="admin-detail-value">{displayValue(patient.age)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Gender</label>
            <div className="admin-detail-value">{displayValue(patient.gender)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Preferred Pronouns</label>
            <div className="admin-detail-value">{displayValue(patient.pronouns)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Location</label>
            <div className="admin-detail-value">{displayValue(patient.location)}</div>
          </div>
        </div>
      </div>

      {/* Part 2: Mental Health History */}
      <div className="admin-section-card">
        <h3 className="admin-section-title">Part 2: Mental Health History</h3>
        <div className="admin-mental-health-list">
          <div className="admin-detail-group admin-full-width">
            <label className="admin-detail-label">Current Mental Health Diagnosis</label>
            <div className="admin-detail-value">{displayValue(patient.diagnosis)}</div>
          </div>
          <div className="admin-detail-group admin-full-width">
            <label className="admin-detail-label">Treatment History</label>
            <div className="admin-detail-value">{displayValue(patient.treatmentHistory)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Frequency of Symptoms</label>
            <div className="admin-detail-value">{displayValue(patient.symptomsFrequency)}</div>
          </div>
        </div>
      </div>

      {/* Part 3: Daily Life & Lifestyle */}
      <div className="admin-section-card">
        <h3 className="admin-section-title">Part 3: Daily Life & Lifestyle</h3>
        <div className="admin-lifestyle-grid">
          <div className="admin-detail-group admin-full-width">
            <label className="admin-detail-label">Sleep Patterns</label>
            <div className="admin-detail-value">{displayValue(patient.sleepPatterns)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Exercise and Physical Activity</label>
            <div className="admin-detail-value">{displayValue(patient.exerciseFrequency)}</div>
          </div>
          <div className="admin-detail-group admin-full-width">
            <label className="admin-detail-label">Dietary Habits</label>
            <div className="admin-detail-value">{displayValue(patient.dietaryPatterns)}</div>
          </div>
        </div>
      </div>

      {/* Part 4: Emergency Contact Person */}
      <div className="admin-section-card">
        <h3 className="admin-section-title">Part 4: Emergency Contact Person</h3>
        <div className="admin-emergency-contact-grid">
          <div className="admin-detail-group">
            <label className="admin-detail-label">Name</label>
            <div className="admin-detail-value">{displayValue(patient.emergencyName)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Relationship to User</label>
            <div className="admin-detail-value">{displayValue(patient.emergencyRelationship)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Phone Number</label>
            <div className="admin-detail-value">{displayValue(patient.emergencyPhone)}</div>
          </div>
          <div className="admin-detail-group">
            <label className="admin-detail-label">Email Address</label>
            <div className="admin-detail-value">{displayValue(patient.emergencyEmail)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Mood Check-ins Tab Component
const AdminMoodCheckinsTab = ({ 
  patient,
  moodData,
  moodLoading,
  moodError,
  selectedDays,
  setSelectedDays,
  fetchPatientMoodData,
  moodSvgUrls,
  moodColors,
  tenantId
}) => {
  const navigate = useNavigate(); // Add this import at the top of AdminPatientDetails.jsx

  // Helper functions
  const formatMoodLabel = (moodKey) => {
    return moodKey?.charAt(0).toUpperCase() + moodKey?.slice(1) || 'Unknown';
  };

  const getMoodSvg = (moodKey) => {
    return moodSvgUrls[moodKey] || moodSvgUrls['okay'];
  };

  const getMoodColor = (moodKey) => {
    return moodColors[moodKey] || '#FFC107';
  };

  const formatDayName = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const getMoodLabelColor = (moodKey) => {
  const moodLabelColors = {
    great: '#3A8314',
    good: '#7A9241',
    okay: '#9A8C06',
    struggling: '#A26133',
    upset: '#944747'
  };
  return moodLabelColors[moodKey] || '#000000';
};

  const getAllMoodsWithData = () => {
  const allMoods = ['great', 'good', 'okay', 'struggling', 'upset'];
  const result = {};
  
  // Add all moods with default zero values
  allMoods.forEach(mood => {
    result[mood] = { count: 0, percentage: 0 };
  });
  
  // Override with actual data if it exists
  if (moodDistribution) {
    Object.entries(moodDistribution).forEach(([key, data]) => {
      result[key] = data;
    });
  }
  
  return result;
};

  // Get last check-in time and date
  const getLastCheckIn = () => {
    if (!moodData?.moodHistory || moodData.moodHistory.length === 0) {
      return { date: 'N/A', time: 'N/A' };
    }
    
    const lastEntry = moodData.moodHistory[0]; // Assuming sorted by most recent
    const lastDate = new Date(lastEntry.timestamp || lastEntry.createdAt);
    
    return {
      date: lastDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: lastDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  // Get risk level indicator for patients with sentiment analysis
  const getRiskLevelIndicator = (sentimentSummary) => {
    if (!sentimentSummary) return null;
    
    const riskLevel = sentimentSummary.latestRiskLevel;
    const colors = {
      POSITIVE: '#4CAF50',
      LOW: '#8BC34A',
      MILD: '#FFC107',
      MODERATE: '#FF9800',
      HIGH: '#F44336',
      CRISIS: '#D32F2F',
      UNKNOWN: '#9E9E9E'
    };

    if (riskLevel && riskLevel !== 'UNKNOWN') {
      return (
        <span 
          className="admin-risk-indicator"
          style={{ 
            backgroundColor: colors[riskLevel],
            color: 'white',
            padding: '2px 6px',
            borderRadius: '8px',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: '8px'
          }}
        >
          {riskLevel}
        </span>
      );
    }
    return null;
  };

  const handleViewMoodAnalysis = () => {
  console.log('🔍 ADMIN: Navigating to mood analysis for patient:', patient);
  
  // Format patient data for the admin mood analysis page
  const patientData = {
    patientId: patient._id,
    patientName: `${patient.firstName} ${patient.lastName}`,
    patientEmail: patient.email,
    totalLogs: moodData?.keyMetrics?.totalLogs || 0,
    registrationDate: patient.createdAt,
    averageRating: moodData?.keyMetrics?.averageMoodScore || 0,
    lastMood: moodData?.moodHistory?.[0] || null,
    accountStatus: patient.accountStatus || 'Active',
    date: new Date().toISOString()
  };
  
  console.log('📊 ADMIN: Patient data for mood analysis:', patientData);
  
  // Navigate to admin mood analysis page
  navigate(`/admin/patients/${patient._id}/mood-analysis`, {
    state: { 
      patientData,
      tenantId: tenantId // This is the tenantId from searchParams at the top of your component
    }
  });
};

  if (moodLoading) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-loading-container">
          <div className="admin-loading-spinner"></div>
          <p>Loading mood check-ins...</p>
        </div>
      </div>
    );
  }

  if (moodError) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-error-container">
          <div className="admin-error-alert">
            <span className="admin-error-icon">⚠️</span>
            <span className="admin-error-message">{moodError}</span>
          </div>
          <button className="admin-primary-button" onClick={fetchPatientMoodData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!moodData || !moodData.keyMetrics) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-mood-checkins-container">
          {/* Header */}
          <div className="admin-mood-checkins-header">
            <div className="admin-header-right">
              <div className="admin-time-filter">
                <label>Show data for</label>
                <select 
                  value={selectedDays} 
                  onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                  className="admin-days-select"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
              </div>
            </div>
          </div>

          <div className="admin-empty-state">
            <h3>No Mood Check-ins Available</h3>
            <p>{patient.firstName} hasn't submitted any mood check-ins during the selected time period.</p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              Mood check-ins will appear here once {patient.firstName} starts using the mood tracking feature.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { keyMetrics, dailyOverview, moodDistribution, moodHistory } = moodData;
  const lastCheckIn = getLastCheckIn();

  return (
    <div className="admin-tab-panel">
      <div className="admin-mood-checkins-container">
        {/* This Week's Mood Summary */}
        <div className="admin-weekly-insights-header">
          <h2>This Week's Mood Summary</h2>
        </div>

        {/* Updated Key Metrics - Following Doctor's Design */}
        <div className="admin-key-metrics-section">
          <div className="admin-metrics-grid">
            <div className="admin-metric-card">
              <h3>Number of Logs</h3>
              <div className="admin-metric-card-inside">
                <div className="admin-metric-value">{keyMetrics?.totalLogs || 0}</div>
                <div className="admin-metric-label">This Week</div>
              </div>
            </div>
            <div className="admin-metric-card">
              <h3>This Week's Average</h3>
              <div className="admin-metric-card-inside">
                <div className="admin-metric-value" 
                        style={{
                          background: 'linear-gradient(90deg, #938400 0%, #EFD700 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          fontWeight: 'bold'
                        }}>
                      {keyMetrics?.averageMoodScore || 0}/5
                    </div>
                <div className="admin-metric-label">Average mood score</div>
              </div>
            </div>
            <div className="admin-metric-card">
              <h3>Last Check-in</h3>
              <div className="admin-metric-card-inside">
                <div className="last-checkin-time-value" style={{ fontSize: '60px' }}>
                  {lastCheckIn.time}
                </div>
                <div className="admin-metric-label">{lastCheckIn.date}</div>
              </div>
            </div>
            <div className="admin-metric-card">
              <h3>Most Frequent Mood</h3>
              <div className="admin-emotional-trends">
                {keyMetrics?.topEmotionalTrends?.length > 0 ? (
                  <div className="admin-trend-item">
                    <div className="admin-emotional-trends-container-inside-emoji">
                      <img 
                        src={getMoodSvg(keyMetrics.topEmotionalTrends[0].mood?.toLowerCase())} 
                        alt={keyMetrics.topEmotionalTrends[0].mood}
                        className="admin-trend-svg"
                      />
                    </div>
                    <div className="admin-emotional-trends-container-inside">
                      <span className="admin-trend-label">{keyMetrics.topEmotionalTrends[0].mood}</span>
                    </div>
                  </div>
                ) : (
                  <div className="admin-trend-item">
                    <div className="admin-emotional-trends-container-inside-emoji">
                      <img 
                        src={getMoodSvg('okay')} 
                        alt="okay" 
                        className="admin-trend-svg"
                      />
                    </div>
                    <div className="admin-emotional-trends-container-inside">
                      <span className="admin-trend-label">No data</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="admin-header-divider"></div>

        {/* Daily Overview */}
        {dailyOverview && dailyOverview.length > 0 && (
          <div className="admin-daily-overview-section">
            <h2 className="admin-daily-overview-title">Daily Overview</h2>
            <div className="admin-daily-overview-grid">
              {dailyOverview.map((day, index) => (
                <div key={index} className="admin-daily-item">
                  <div className="admin-daily-date">
                    {new Date(day.date || day.dateString).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="admin-daily-day-name">{formatDayName(day.date)}</div>
                  <div className="admin-daily-mood-svg">
                    <img 
                      src={getMoodSvg(day.mostFrequentMood)} 
                      alt={day.mostFrequentMood}
                      className="admin-daily-svg"
                    />
                  </div>
                  <div className="admin-daily-stats">
                    <div className="admin-daily-score">{day.averageMoodScore}</div>
                    <div className="admin-daily-entries">{day.totalEntries} logs</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mood Distribution */}
        <div className="admin-mood-distribution-section">
          <div className="admin-mood-distribution-container">
            <h2>Mood Distribution</h2>
            <div className="admin-mood-distribution-grid">
              {Object.entries(getAllMoodsWithData()).map(([moodKey, data]) => (
                <div key={moodKey} className={`admin-mood-distribution-item ${moodKey}`}>
                  <div className="admin-mood-info">
                    <img 
                      src={getMoodSvg(moodKey)} 
                      alt={moodKey}
                      className="admin-mood-svg-icon"
                    />
                    <span className="admin-mood-label" style={{ color: getMoodLabelColor(moodKey) }}>
                      I'm {moodKey}
                    </span>
                  </div>
                  
                  {/* Separate container for log count */}
                  <div className="admin-log-count-container">
                    <div className="admin-mood-count">{data.count} logs</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="admin-header-divider"></div>

        {/* Mood History Table with NLP Analysis */}
        {moodHistory && moodHistory.length > 0 && (
          <div className="admin-mood-history-section">
            <h2>Mood Check-in History</h2>
            <div className="admin-history-controls">
              <div className="admin-search-container">
                <input 
                  type="text" 
                  placeholder="Search mood entries..." 
                  className="admin-search-input"
                  // Add search functionality if needed
                />
              </div>
              <div className="admin-filter-controls">
                <select 
                  className="admin-filter-select"
                  // Add sorting functionality if needed
                >
                  <option value="date_desc">Sort by: Date (Newest)</option>
                  <option value="date_asc">Sort by: Date (Oldest)</option>
                  <option value="mood">Sort by: Mood Rating</option>
                  <option value="average">Sort by: Average Rating</option>
                </select>
              </div>
              <button className="admin-export-button">
                <span>📄</span> Export PDF
              </button>
            </div>

            <div className="admin-mood-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Average</th>
                    <th>Total Logs</th>
                    <th>End-of-Day Mood Check-in Log</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Group mood history by date for table display */}
                  {(() => {
                    // Group entries by date
                    const groupedByDate = moodHistory.reduce((groups, entry) => {
                      const date = new Date(entry.timestamp || entry.createdAt).toDateString();
                      if (!groups[date]) {
                        groups[date] = [];
                      }
                      groups[date].push(entry);
                      return groups;
                    }, {});

                    return Object.entries(groupedByDate).map(([date, dayEntries]) => {
                      // Calculate day statistics
                      const avgRating = dayEntries.reduce((sum, entry) => sum + (entry.moodRating || 0), 0) / dayEntries.length;
                      const lastMood = dayEntries[dayEntries.length - 1]; // Assuming last entry is end-of-day
                      
                      return (
                        <tr key={date}>
                          <td>{new Date(date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}</td>
                          <td>
                            <div className="admin-patient-name-cell">
                              {patient.firstName} {patient.lastName}
                              {/* Add risk level indicator if available */}
                              {getRiskLevelIndicator(null /* Add sentiment summary if available */)}
                            </div>
                          </td>
                          <td>{avgRating ? `${avgRating.toFixed(1)}/5` : 'N/A'}</td>
                          <td>{dayEntries.length} logs</td>
                          <td>
                            {lastMood ? (
                              <span className={`admin-mood-indicator ${lastMood.moodKey}`}>
                                <img 
                                  src={getMoodSvg(lastMood.moodKey)} 
                                  alt={lastMood.moodKey}
                                  className="admin-mood-svg-small"
                                />
                                {formatMoodLabel(lastMood.moodKey)}
                              </span>
                            ) : (
                              <span className="admin-mood-indicator okay">
                                <img 
                                  src={getMoodSvg('okay')} 
                                  alt="okay"
                                  className="admin-mood-svg-small"
                                />
                                No recent mood
                              </span>
                            )}
                          </td>
                          <td>
                            <button 
                              className="admin-action-button admin-view-mood-button"
                              onClick={handleViewMoodAnalysis}
                              title="View NLP Mood Analysis"
                            >
                              <svg className="admin-button-icon" viewBox="0 0 24 24" fill="none" style={{width: '22px', height: '22px'}}>
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#548170" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="12" r="3" stroke="#548170" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="admin-button-text">Analyze</span>
                            </button>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                  {Object.keys(moodHistory.reduce((groups, entry) => {
                    const date = new Date(entry.timestamp || entry.createdAt).toDateString();
                    groups[date] = true;
                    return groups;
                  }, {})).length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        No mood check-in data available for {patient.firstName}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 🆕 NEW: Admin Journal Tab Component
const AdminJournalTab = ({ 
  patient,
  journalData,
  journalLoading,
  journalError,
  selectedDays,
  setSelectedDays,
  fetchPatientJournalData
}) => {
  const navigate = useNavigate();

  // Helper functions
  const formatDateSafe = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTimeSafe = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  const getSentimentClass = (sentiment) => {
    const sentimentLower = sentiment?.toLowerCase() || 'neutral';
    switch (sentimentLower) {
      case 'positive': return 'admin-sentiment-positive';
      case 'negative': return 'admin-sentiment-negative';
      default: return 'admin-sentiment-neutral';
    }
  };

  const getSentimentColor = (sentiment) => {
    const sentimentLower = sentiment?.toLowerCase() || 'neutral';
    switch (sentimentLower) {
      case 'positive': return '#4CAF50';
      case 'negative': return '#F44336';
      default: return '#FFC107';
    }
  };

  const getMoodEmoji = (mood) => {
    const moodLower = mood?.toLowerCase() || 'okay';
    switch (moodLower) {
      case 'great': return '😄';
      case 'good': return '😊';
      case 'okay': return '😐';
      case 'struggling': return '😟';
      case 'upset': return '😔';
      case 'positive': return '😊';
      case 'negative': return '😔';
      default: return '😐';
    }
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return 'No content available';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getEmotionTags = (entry) => {
    if (!entry.sentiment || !entry.sentiment.emotions) {
      return [];
    }
    
    return entry.sentiment.emotions.map(emotion => 
      typeof emotion === 'string' ? emotion : emotion.name
    ).filter(Boolean);
  };

  const handleViewEntry = (entryId) => {
    navigate(`/admin/journal-entries/${entryId}`);
  };

  if (journalLoading) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-loading-container">
          <div className="admin-loading-spinner"></div>
          <p>Loading journal entries...</p>
        </div>
      </div>
    );
  }

  if (journalError) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-error-container">
          <div className="admin-error-alert">
            <span className="admin-error-icon">⚠️</span>
            <span className="admin-error-message">{journalError}</span>
          </div>
          <button className="admin-primary-button" onClick={fetchPatientJournalData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!journalData || !journalData.entries || journalData.entries.length === 0) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-journal-container">
          <div className="admin-journal-empty-state">
            <h3>No Journal Entries Available</h3>
            <p>{patient.firstName} hasn't written any journal entries during the selected time period.</p>
          </div>
        </div>
      </div>
    );
  }

  const { entries } = journalData;

  return (
    <div className="admin-tab-panel">
      <div className="admin-journal-container">
        <div className="admin-journal-entries-section">
          <h2>Journal Entries History</h2>
          
          <div className="admin-journal-table">
            {/* Table Header */}
            <div className="admin-journal-table-header">
              <div className="admin-journal-header-cell">Date & Time</div>
              <div className="admin-journal-header-cell">Patient</div>
              <div className="admin-journal-header-cell">Entry Title</div>
              <div className="admin-journal-header-cell">Preview</div>
              <div className="admin-journal-header-cell">Words</div>
              <div className="admin-journal-header-cell">Actions</div>
            </div>

            {/* Table Rows */}
            {entries.map((entry, index) => {
              const wordCount = entry.wordCount || 
                               (entry.content || entry.rawText || entry.text || '').split(/\s+/).length;
              
              return (
                <div key={entry._id || index} className="admin-journal-table-row">
                  <div className="admin-journal-table-cell">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className="admin-journal-date-cell">
                        {formatDateSafe(entry.createdAt || entry.date)}
                      </span>
                      <span className="admin-journal-time-cell">
                        {formatTimeSafe(entry.createdAt || entry.date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="admin-journal-table-cell">
                    <strong>{patient.firstName} {patient.lastName}</strong>
                  </div>
                  
                  <div className="admin-journal-table-cell">
                    <div className="admin-journal-title">
                      <strong>{entry.title || entry.templateName || 'Untitled Entry'}</strong>
                      {entry.templateName && entry.title !== entry.templateName && (
                        <div className="admin-journal-title-template">
                          Template: {entry.templateName}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="admin-journal-table-cell">
                    <div className="admin-journal-preview">
                      {truncateText(entry.content || entry.rawText || entry.text, 120)}
                    </div>
                  </div>
                  
                  <div className="admin-journal-table-cell" style={{ textAlign: 'center' }}>
                    <strong>{wordCount}</strong>
                  </div>
                  
                  <div className="admin-journal-table-cell">
                    <div className="admin-actions-cell">
                      <button
                        className="admin-action-btn admin-view-btn"
                        onClick={() => handleViewEntry(entry._id)}
                        title="View full journal entry"
                      >
                        👁️ View
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};


export default AdminPatientDetails;